import {
  arrayUnion,
  collection,
  doc,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { CustomAnalyticsEvent } from '@superfunnel/types/account';
import { AccountAiSettings } from '@superfunnel/types/account-ai-settings';
import { AccountBrand } from '@superfunnel/types/account-brand';
import { AccountConfig } from '@superfunnel/types/account-config';

import { db, functions } from '../Firebase';

export const updateAccountBrand = async (
  accountId: string,
  data: Partial<AccountBrand>,
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const accountBrandRef = doc(
    collection(accountRef, 'accountBrand'),
    'default',
  );

  return setDoc(accountBrandRef, data, { mergeFields: Object.keys(data) });
};

export const updateAccountConfig = async (
  accountId: string,
  data: Partial<AccountConfig>,
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const accountConfigRef = doc(
    collection(accountRef, 'accountConfig'),
    'default',
  );

  const mergeFields = Object.entries(data).flatMap(([key, value]) => {
    if (key === 'seo') {
      const subFields = Object.keys(value);
      return subFields.map((subField) => `${key}.${subField}`);
    }

    return key;
  });

  return setDoc(accountConfigRef, data, { mergeFields: mergeFields });
};

/**
 * Update the account-wide list of analytics custom (conversion) events.
 * Events are stored directly on the account document.
 */
export const updateAccountAnalyticsCustomEvents = async (
  accountId: string,
  analyticsCustomEvents: CustomAnalyticsEvent[],
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);

  return setDoc(
    accountRef,
    { analyticsCustomEvents },
    { mergeFields: ['analyticsCustomEvents'] },
  );
};

export const addAccountAnalyticsCustomEvent = async (
  accountId: string,
  event: CustomAnalyticsEvent,
) =>
  updateDoc(doc(collection(db, 'accounts'), accountId), {
    analyticsCustomEvents: arrayUnion(event),
  });

export const updateAccountAiSettings = async (
  accountId: string,
  data: Partial<AccountAiSettings>,
) => {
  const accountsColRef = collection(db, 'accounts');
  const accountRef = doc(accountsColRef, accountId);
  const accountAiSettingsRef = doc(
    collection(accountRef, 'accountAiSettings'),
    'default',
  );

  return setDoc(accountAiSettingsRef, data, { mergeFields: Object.keys(data) });
};

export const getSSOAuthProvider = async (email: string) => {
  const fn = httpsCallable(functions, 'accounts/getSsoAuthProvider');
  const res = await fn({ email });

  return res.data;
};

export const scrapeAccountData = async (
  accountId: string,
  url: string,
  userId: string,
  extractFields: ('brand' | 'aiSettings')[],
): Promise<any> => {
  const fn = httpsCallable(functions, 'scrape/scrapeAccountData');
  const res = await fn({
    accountId,
    url,
    userId,
    override: true,
    extractFields,
  });

  return res.data;
};

export const createAccount = async (data: {
  websiteUrl: string;
  name: string | null;
  slug: string | null;
  skipOnboarding?: boolean;
}): Promise<{
  ok: boolean;
  accountId: string;
  slug: string;
  screenshotUrl?: string;
}> => {
  const websiteUrl = data.websiteUrl.startsWith('http')
    ? data.websiteUrl.replaceAll('http://', 'https://')
    : `https://${data.websiteUrl}`;
  const fn = httpsCallable<any, any>(functions, 'accounts/createAccount');
  const res = await fn({ ...data, websiteUrl });

  return res.data;
};

export const completeOnboarding = async (
  accountId: string,
): Promise<{ ok: boolean }> => {
  const fn = httpsCallable<any, any>(functions, 'accounts/completeOnboarding');
  const res = await fn({ accountId });

  return res.data;
};
